import React, { useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";

import useVendorConfigs from "../../../hooks/useVendorConfigs";
import { VendorConfig } from "../../../types/onboarding";
import {
  assetFileName,
  isVendorConfigIncomplete,
} from "../../../utils/vendorConfigs";
import VendorConfigDialog from "./components/VendorConfigDialog";

/**
 * Manufacturer configuration catalogue (CWA-91, US3).
 *
 * Decides what each customer receives with their configuration email: whether a
 * setup guide and/or security certificate is required per wallbox manufacturer,
 * and the files themselves. An entry that requires a file it does not have is
 * flagged, because the customer would otherwise get an incomplete email.
 */
const VendorConfigsPage: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { configs, loading, error, create, update, upload } =
    useVendorConfigs(toast);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<VendorConfig | null>(null);

  const openAdd = () => {
    setEditing(null);
    setDialogVisible(true);
  };
  const openEdit = (config: VendorConfig) => {
    setEditing(config);
    setDialogVisible(true);
  };

  // The dialog needs the freshest entry so file statuses update after an upload
  // without closing and reopening it.
  const editingLive = editing
    ? configs.find((c) => c.vendor_id === editing.vendor_id) ?? editing
    : null;

  // Files are shown by name only — the stored value is an internal storage key.
  const fileBody = (storageKey: string | null, required: boolean) => {
    const name = assetFileName(storageKey);
    if (name) return <Tag severity="success" value={name} />;
    if (required) return <Tag severity="danger" value="Missing" />;
    return <span className="text-500">Not needed</span>;
  };

  const statusBody = (config: VendorConfig) =>
    isVendorConfigIncomplete(config) ? (
      <Tag severity="danger" value="Incomplete" />
    ) : (
      <Tag severity="success" value="Ready" />
    );

  const actionsBody = (config: VendorConfig) => (
    <Button
      icon="pi pi-pencil"
      rounded
      text
      aria-label={`Edit ${config.vendor_name}`}
      onClick={() => openEdit(config)}
    />
  );

  return (
    <div className="card onboarding-page">
      <Toast ref={toast} />

      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <h2 className="m-0">Manufacturer Configuration</h2>
        <Button label="Add manufacturer" icon="pi pi-plus" onClick={openAdd} />
      </div>

      <p className="text-500 mt-0 mb-3">
        What each customer receives with their configuration email, by wallbox
        manufacturer.
      </p>

      {error && (
        <Message
          severity="error"
          text="The manufacturer configurations could not be loaded."
          className="mb-3 w-full"
        />
      )}

      <DataTable
        value={configs}
        loading={loading}
        dataKey="vendor_id"
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        emptyMessage="No manufacturers configured yet."
      >
        <Column field="vendor_name" header="Manufacturer" sortable />
        <Column field="vendor_id" header="Identifier" sortable />
        <Column header="Status" body={statusBody} />
        <Column
          header="Setup guide"
          body={(c: VendorConfig) =>
            fileBody(c.pdf_guide_s3_key, c.requires_pdf_guide)
          }
        />
        <Column
          header="Certificate"
          body={(c: VendorConfig) =>
            fileBody(c.certificate_s3_key, c.requires_certificate)
          }
        />
        <Column
          header="Certificate type"
          body={(c: VendorConfig) => c.certificate_type ?? "—"}
        />
        <Column
          header="Email template"
          body={(c: VendorConfig) => fileBody(c.email_template_s3_key, false)}
        />
        <Column header="" body={actionsBody} style={{ width: "5rem" }} />
      </DataTable>

      <VendorConfigDialog
        visible={dialogVisible}
        config={editingLive}
        onHide={() => setDialogVisible(false)}
        onSubmit={(payload) =>
          editingLive
            ? update(editingLive.vendor_id, {
                vendor_name: payload.vendor_name,
                requires_pdf_guide: payload.requires_pdf_guide,
                requires_certificate: payload.requires_certificate,
                certificate_type: payload.certificate_type,
              })
            : create(payload)
        }
        onUpload={upload}
      />
    </div>
  );
};

export default VendorConfigsPage;
